import { useProjects } from '@hooks/useProjects';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Org-wide banner for `past_due` / `suspended` subscriptions.
 *
 * This is now the PRIMARY surface for `past_due`, not just a cross-project
 * hint. The per-page paywall interstitial that used to cover the selected
 * project is gone: `past_due` deliberately keeps the workspace usable so
 * operators can still reply to customers (see ProjectBroadcastGuard — outbound
 * broadcasts are blocked server-side, inbound and replies keep flowing), and
 * `suspended` is handled by the full-screen WalletBlockerOverlay instead.
 *
 * So the banner's job is to be noticed and to route to the fix: /projects
 * carries the Reactivate action.
 *
 * Hidden when nothing is paywalled. Not dismissible — it re-evaluates from
 * server state and disappears on its own once billing is resolved.
 */
export function PastDueBanner(): JSX.Element | null {
  const { projects } = useProjects();

  const pastDue = projects.filter((p) => p.subscription?.status === 'past_due').length;
  const suspended = projects.filter((p) => p.subscription?.status === 'suspended').length;
  const count = pastDue + suspended;

  if (count === 0) return null;

  const noun = count === 1 ? 'WhatsApp number needs' : 'WhatsApp numbers need';
  // Name the actual state. Calling a suspended number "past due" sent people
  // hunting for an overdue amount that does not exist — the sub is paused, and
  // the fix is to reactivate, not to pay off a balance.
  const stateLabel =
    suspended > 0 && pastDue > 0
      ? 'past due or paused'
      : suspended > 0
        ? 'paused'
        : 'past due';

  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-4 border-b border-amber-200 bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] px-6 py-2.5 text-[13px] text-amber-900"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle size={14} strokeWidth={2} className="shrink-0 text-amber-600" />
        <span>
          <span className="font-semibold">{count}</span> {noun} attention — your
          subscription{count === 1 ? ' is' : 's are'} {stateLabel}.
        </span>
      </div>
      <Link
        to="/projects"
        className="shrink-0 font-semibold text-amber-900 underline-offset-2 hover:underline"
      >
        View details &rarr;
      </Link>
    </div>
  );
}
